import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
    AuthenticationMethods,
    ChargingProcessService,
    ServiceDto,
    ServiceService,
    ServiceType,
    TariffDto,
    TariffService
} from '../../_services/configuration-services';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal, NgbModalConfig} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';
import {faCircle, faPen} from '@fortawesome/free-solid-svg-icons';
import {NotificationsService} from '../../_shared/notifications.service';
import {TranslateService} from '@ngx-translate/core';
import {AppRoles, PermissionKeys} from '../../app.constants';
import {ActivateHydrowashModalComponent} from './activate-hydrowash-modal/activate-hydrowash-modal.component';
import {environment} from '../../../environments/environment';
import {ServiceIconUrlBuilder} from '../../_shared/service-icon-url-builder';
import {MsalService} from '@azure/msal-angular';
import {PurchaseServiceModalComponent} from './purchase-service-modal/purchase-service-modal.component';
import {RolesService} from '../../_shared/roles-service';
import {DateUtils} from '../../_shared/date-utils';
import {TranslateUtils} from '../../_shared/translate-utils';
import {LoaderService} from '../../_shared/loader-service';
import {
    ExportFalconicStatisticModalComponent
} from './falconic/export-falconic-statistic-modal/export-falconic-statistic-modal.component';
import {ActivateServiceModalComponent} from './activate-service-modal/activate-service-modal.component';
import {BarcodeFormat} from '@zxing/library';
import {MeasuringPointsCockpitComponent} from './measuring-points-cockpit/measuring-points-cockpit.component';
import {ActivationManagementComponent} from './activation-management/activation-management.component';
import {
    ActivateChargingProcessModalComponent
} from './activate-charging-process-modal/activate-charging-process-modal.component';
import {ChargingProcessManagementComponent} from './charging-process-management/charging-process-management.component';
import {ConfirmModalService} from '../../_shared/_components/confirm-modal/confirm-modal.component';
import {ActivateDumpsterModalComponent} from './dumpster/activate-dumpster-modal/activate-dumpster-modal.component';
import {CustomerConfigService} from '../../_shared/customer-config-service';
import {BackofficePermissionsService} from "../../_shared/backoffice-permissions.service";
import {ServiceContextService} from '../../_shared/service-context.service';

@Component({
    selector: 'app-view-service',
    templateUrl: './view-service.component.html',
    styleUrls: ['./view-service.component.scss'],
    providers: [NgbModalConfig, NgbModal],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewServiceComponent implements OnInit, OnDestroy {

    @ViewChild('measuringPointsCockpitComponent') measuringPointsCockpitComponent: MeasuringPointsCockpitComponent | null;
    @ViewChild('activationManagementComponent') activationManagementComponent: ActivationManagementComponent | null;
    @ViewChild('chargingProcessManagementComponent') chargingProcessManagementComponent: ChargingProcessManagementComponent | null;

    protected readonly PermissionKeys = PermissionKeys;

    // Status retrieve by Datatrans to display the transaction result
    datatransTransactionStatus = '';

    buildIconUrl = ServiceIconUrlBuilder.buildIconUrl;

    qrCodeReaderEnabled: boolean;
    hasPermission: boolean;
    loggedIn: boolean;
    displayCameraAccessMessage: boolean;
    cameraIsActivated: boolean;

    serviceId!: string;
    deviceIdentifierToActivate!: string;
    evseIndexNumber: string | null; // For charging stations activation
    service!: ServiceDto;
    customerId: string | null = null;
    lowestTariff: TariffDto | null;

    displayDescription = false;
    displayInstruction = false;

    // icons
    faPen = faPen;
    faCircle = faCircle;

    // Enums
    AppRoles = AppRoles;
    AuthenticationMethods = AuthenticationMethods;
    ServiceType = ServiceType;
    BarcodeFormat = BarcodeFormat;

    dateFormat = DateUtils.dateFormat;
    DateUtils = DateUtils;

    crtLang = TranslateUtils.defaultLanguage;
    private langChangeSubscription: Subscription;

    constructor(private readonly serviceService: ServiceService,
                private readonly tariffService: TariffService,
                private readonly route: ActivatedRoute,
                private readonly translateService: TranslateService,
                private readonly notificationService: NotificationsService,
                private readonly confirmService: ConfirmModalService,
                private readonly modalService: NgbModal,
                private readonly authService: MsalService,
                private readonly rolesService: RolesService,
                private readonly router: Router,
                private readonly loaderService: LoaderService,
                private readonly chargingProcessService: ChargingProcessService,
                private readonly customerConfigService: CustomerConfigService,
                private readonly backofficePermissionsService: BackofficePermissionsService,
                private readonly serviceContextService: ServiceContextService) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange
            .subscribe(_ => {
                const newLang = this.translateService.currentLang;
                if (newLang !== this.crtLang) {
                    this.crtLang = newLang;
                    window.location.reload();
                }
            });

        this.loggedIn = this.authService.instance.getAllAccounts().length > 0;

        this.serviceId = this.route.snapshot.params['serviceId'];
        this.deviceIdentifierToActivate = this.route.snapshot.params['measuringPointId'] ?? this.route.snapshot.params['externalChargingStationId'];
        this.evseIndexNumber = this.route.snapshot.params['evseIndex'];

        this.datatransTransactionStatus = this.route.snapshot.queryParams['datatransStatus'];

        this.fetchService();

        this.stopChargeForVisitor(this.router.url);
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
        this.modalService.dismissAll('destroy');
        this.serviceContextService.clear();
    }

    manageService(): void {
        this.router.navigate(['/services/manage/' + this.serviceId]);
    }

    back(): void {
        this.router.navigate(['/services']);
    }

    formatTimeStringToDisplay(time: string): string {
        return time.slice(0, -3).replace(/:/, 'h');
    }

    async scanSuccessHandler(qrCodeValue: string): Promise<void> {
        this.qrCodeReaderEnabled = false;
        this.cameraIsActivated = false;

        // Special case for Hydrowash service
        if (this.service.serviceConfig.serviceType === ServiceType.Hydrowash) {
            const qrCodeArray = qrCodeValue.split(':');
            if (qrCodeArray[0] === environment.qrCodeEnvPrefix) {
                if (qrCodeArray[1] === 'Hydrowash') {
                    this.openHydrowashActivationModal(qrCodeArray[2]);
                    return;
                }
            }

        }
        else if (this.service.serviceConfig.serviceType === ServiceType.ChargingStations) {
            const segments = qrCodeValue.split('/');

            if (segments.length === 10 && segments[4] === 'services' && segments[6] === 'start-charge' && segments[8] === 'evse') {

                const externalChargingStationId: string = segments[7];

                const evseIndex: number | null = parseInt(segments[9]) ?? null;

                if (evseIndex) {
                    this.openChargingProcessActivationModal(externalChargingStationId, evseIndex);

                    return;
                }
            }
        }
        else { // All other services
            let qrCodeServiceId: string;

            if (qrCodeValue.includes('/services/')) {
                // Public access link
                qrCodeServiceId = qrCodeValue.split('/services/')[1].split('/activate/')[0] ?? 'unknown';
            } else {
                // Private access link
                qrCodeServiceId = qrCodeValue.split('/activate/')[0] ?? 'unknown';
            }

            if(qrCodeServiceId === 'unknown') {
                this.notificationService.error({
                    title: 'error.qrcode.unknown.title',
                    message: 'error.qrcode.unknown.message'
                })
                return;
            }

            if (qrCodeServiceId !== this.serviceId) {
                this.serviceId = qrCodeServiceId;
                await this.router.navigateByUrl('/', { skipLocationChange: true });
                await this.router.navigate([`services/${qrCodeServiceId}`]);
            }

            this.deviceIdentifierToActivate = qrCodeValue.split('/activate/')[1] ?? 'unknown';
            this.openServiceActivationModal();
            return;
        }

        this.notificationService.error({
            title: 'error.qrcode.unknown.title',
            message: 'error.qrcode.unknown.message'
        });
    }

    openServiceActivationModal(): void {

        if (this.hasServiceAccessForServiceType(this.service?.serviceConfig.serviceType)) {
            if (this.deviceIdentifierToActivate && this.lowestTariff) {
                if (this.service.serviceConfig.serviceType === ServiceType.WasteManagement) {
                    const modal = this.modalService.open(ActivateDumpsterModalComponent, {centered: true});
                    modal.componentInstance.deviceIdentifierToActivate = this.deviceIdentifierToActivate;

                    modal.result.then(error => {
                        // Remove the params in URL
                        this.clearUrlAfterActivation();

                        if (error) {
                            if (error.status === 404) {
                                this.notificationService.error({
                                    title: 'error.qrcode.unknown.title',
                                    message: 'error.qrcode.unknown.message'
                                });
                            } else {
                                throw error;
                            }
                        }

                    }, error => {
                        // Remove the params in URL
                        if (error !== 'destroy') {
                            this.clearUrlAfterActivation();
                        }
                    });

                } else if (this.service.serviceConfig.serviceType === ServiceType.ChargingStations) {
                    this.openChargingProcessActivationModal(this.deviceIdentifierToActivate, parseInt(this.evseIndexNumber));
                }
                else {
                    const modal = this.modalService.open(ActivateServiceModalComponent, {centered: true});
                    modal.componentInstance.serviceId = this.serviceId;
                    modal.componentInstance.measuringPointId = this.deviceIdentifierToActivate;
                    modal.componentInstance.defaultTariff = this.lowestTariff;
                    modal.componentInstance.hasVisitorAccess = this.service.customRoles.length === 0;
                    modal.componentInstance.activationDuration = this.service.impulseDuration;
                    modal.componentInstance.activationUnit = this.service.impulseDurationUnit;
                    modal.componentInstance.isExploitant = this.isCurrentBackofficeServiceExploitant();

                    modal.result.then(value => {
                        if (value === 'MeasuringPointNotFound') {
                            this.notificationService.error({
                                title: 'error.qrcode.unknown.title',
                                message: 'error.qrcode.unknown.message'
                            });
                        } else {
                            this.activationManagementComponent?.reloadData();
                            this.measuringPointsCockpitComponent?.reloadData();
                        }

                        // Remove the params in URL
                        this.clearUrlAfterActivation();

                    }, error => {
                        // Remove the params in URL
                        if (error !== 'destroy') {
                            this.clearUrlAfterActivation();
                        }
                    });
                }
            }
        }
    }

    openHydrowashActivationModal(trackId: string): void {
        const modal = this.modalService.open(ActivateHydrowashModalComponent, {centered: true});
        modal.componentInstance.trackId = trackId;
        modal.componentInstance.availablePaymentTypes = this.lowestTariff?.paymentTypes;

        modal.result.then(async value => {
            if (value === 'confirm') {
                this.fetchService();
            }
        }, () => { /* catch the rejection */ });
    }

    openChargingProcessActivationModal(externalChargingStationId: string, evseIndex: number): void {
        const modal = this.modalService.open(ActivateChargingProcessModalComponent, {centered: true});

        modal.componentInstance.serviceId = this.serviceId;
        modal.componentInstance.externalChargingStationId = externalChargingStationId;
        modal.componentInstance.evseIndex = evseIndex;
        modal.componentInstance.defaultTariff = this.lowestTariff;
        modal.componentInstance.customerId = this.customerId;

        modal.result.then(async value => {
            if (value === 'success') {
                // Reload charging processes data
                await this.chargingProcessManagementComponent?.clearFilter();
            }
            else if (value === 'ChargingStationNotFound') {
                this.notificationService.error({
                    title: 'error.qrcode.unknown.title',
                    message: 'error.qrcode.unknown.message'
                });
            }

            // Remove the params in URL
            this.clearUrlAfterActivation();
        }, error => {
            // Remove the params in URL
            if (error !== 'destroy') {
                this.clearUrlAfterActivation();
            }
        });
    }

    canScanQrCode() {
        // QR scan is a customer/visitor action: never shown to a Backoffice/Exploitant (incl. SuperAdmin).
        if (this.rolesService.isBackofficeAccount()) {
            return false;
        }
        return !!this.service.authenticationMethods.find(am => am === AuthenticationMethods.Qrcode)
            && !!this.lowestTariff
            && !this.cameraIsActivated
            && this.hasServiceAccessForServiceType(this.service?.serviceConfig.serviceType);
    }

    startQrCodeReader(): void {
        this.qrCodeReaderEnabled = true;
        this.loaderService.start('QrCodeLoader');
    }

    stopQrCodeReader(): void {
        this.cameraIsActivated = false;
        this.qrCodeReaderEnabled = false;
    }

    startBuyProcess(): void {
        const modal = this.modalService.open(PurchaseServiceModalComponent, {centered: true});
        modal.componentInstance.tariff = this.lowestTariff;
        modal.componentInstance.service = this.service;
    }

    exportFalconicStatistics(): void {
        const modal = this.modalService.open(ExportFalconicStatisticModalComponent, {centered: true});
        modal.componentInstance.serviceId = this.serviceId;
    }

    activateMeasuringPoint(measuringPointId: string): void {
        this.deviceIdentifierToActivate = measuringPointId;
        this.openServiceActivationModal();
    }

    permissionResponse(hasPermission: boolean): void {
        this.hasPermission = hasPermission;
        if (!hasPermission) {
            this.loaderService.stop('QrCodeLoader');
            this.qrCodeReaderEnabled = false;
            this.displayCameraAccessMessage = true;
        } else {
            this.displayCameraAccessMessage = false;
            this.cameraIsActivated = true;
        }
    }

    camerasFoundHandler(): void{
        this.loaderService.stop('QrCodeLoader');
    }

    hasRoleToAccessTheService(): boolean {
        if (this.service?.customRoles.length === 0){
            return true;
        }
        return this.rolesService.hasAnyRole(this.service.customRoles.map(r => r.roleKey))
    }

    /**
     * A customer (or public visitor) allowed to use the service for themselves.
     * Backoffice staff are excluded: they act through the exploitant path, never as a customer.
     */
    private hasCustomerOrVisitorAccess(): boolean {
        return this.hasRoleToAccessTheService() && !this.rolesService.isBackofficeAccount();
    }

    hasAccessToBookingCockpit(): boolean {
        return this.service?.bookable && this.service?.items.length > 0 && this.hasServiceAccessForServiceType(ServiceType.Bookable);
    }

    /**
     * Charging process management visibility:
     *  - Customer (or any non-Backoffice authenticated user) — sees their own charges.
     *  - Exploitant of THIS service — sees all charges of the service.
     *  - Backoffice not assigned to this service — hidden (the search endpoint would 403).
     */
    hasAccessToChargingProcessManagement(): boolean {
        return this.hasServiceAccessForServiceType(ServiceType.ChargingStations)
            && this.loggedIn
            && (this.isCurrentBackofficeServiceExploitant() || !this.rolesService.isBackofficeAccount());
    }

    hasRole(role: string): boolean {
        return this.rolesService.hasRole(role);
    }

    hasGestionServicesPermission(): boolean {
        return this.backofficePermissionsService.hasPermission(PermissionKeys.gestionServices);
    }

    isCurrentBackofficeServiceExploitant(): boolean {
        return this.serviceContextService.isCurrentBackofficeServiceExploitant();
    }

    hasServiceEditAccess(): boolean {
        return this.hasGestionServicesPermission() || this.isCurrentBackofficeServiceExploitant();
    }

    hasServiceAccessForServiceType(serviceType: ServiceType): boolean {
        // Check Service Type
        if (this.service?.serviceConfig.serviceType !== serviceType) {
            return false;
        }

        // Only users with GestionServices permission or the service's Exploitant can access an inactive service.
        if (!this.service?.active && !this.hasServiceEditAccess()) {
            return false;
        }

        // Check for service type
        switch (serviceType) {
            // Services only for admin
            case ServiceType.PublicLighting:
                return this.isCurrentBackofficeServiceExploitant();

            // Services only for customers (ignore tariffs)
            case ServiceType.UserAlarm:
                return this.rolesService.hasRoleCustomer() && this.hasRoleToAccessTheService();

            // Services only for the service's Exploitant (ignore tariffs)
            case ServiceType.MooringPlace:
                return this.isCurrentBackofficeServiceExploitant();

            // Services for all (ignore tariffs and availability ranges)
            case ServiceType.Default:
            case ServiceType.WasteManagement:
            case ServiceType.AccessControl:
            case ServiceType.TimeLimited:
            case ServiceType.Webcam:
            case ServiceType.Weather:
            case ServiceType.ChargingStations:
                return this.isCurrentBackofficeServiceExploitant() || this.hasRoleToAccessTheService(); // Check Role + Public

            // Services for all (need tariff set)
            case ServiceType.Hydrowash:
            case ServiceType.Energy:
            case ServiceType.Wifi:
                return !!this.lowestTariff && (this.isCurrentBackofficeServiceExploitant() || this.hasRoleToAccessTheService());

            // Services only for customers and exploitants
            case ServiceType.Bookable:
            case ServiceType.CraningAndWinterizing:
                return !!this.lowestTariff && (this.isCurrentBackofficeServiceExploitant() || this.hasCustomerOrVisitorAccess());
        }

        return false;
    }

    public canExportFalconicStatistic(): boolean {
        return this.service?.serviceConfig.serviceType === ServiceType.WasteManagement &&
            this.isCurrentBackofficeServiceExploitant() &&
            this.customerConfigService.isFalconicActive
    }

    private stopChargeForVisitor(url: string): void {
        const segments = url.split('/');

        if (segments.find(s => s === 'stop-charge')) {
            const chargeProcessId: string = segments[4];

            this.confirmService.confirm({titleKey: 'common.confirmModal.title.disable'}).then(result => {
                if (result === ConfirmModalService.yes) {
                    this.chargingProcessService.deactivateChargingProcessForVisitor(chargeProcessId).pipe().subscribe((): void => {
                        this.notificationService.success({
                            title: 'activations.terminateSuccessMessage',
                            message: 'activations.terminateSuccessExplanation',
                        });
                    });
                }
                this.clearUrlAfterActivation();
            });
        }
    }

    private fetchService(): void {
        this.serviceService.getService(this.serviceId).pipe().subscribe(service => {
            this.service = service;
            this.serviceContextService.setCurrentService(service);
            this.lowestTariff = service.tariffs.find(t => t.defaultTariff);

            // For an exploitant, leave customerId null so tariff lookups stay at the default service tariff
            // instead of being scoped to the exploitant's own account (which is not a customer of the service).
            this.customerId = this.isCurrentBackofficeServiceExploitant() ? null : this.authService.instance.getActiveAccount()?.localAccountId ?? null;

            if (this.customerId) {
                this.tariffService.getTariffForServiceAndCustomer(this.serviceId, this.customerId).pipe().subscribe(tariff => {
                    this.lowestTariff = tariff;
                    // Open the activation modal if necessary
                    this.openServiceActivationModal();
                });

            } else {
                // Open the activation modal if necessary
                this.openServiceActivationModal();
            }

            this.checkDisplayOfLanguageFields();

            // Keep only effective availability range
            this.service.availabilityRanges = this.service.availabilityRanges.filter(a => a.endDate == null || a.endDate > new Date());

            this.service.availabilityRanges.filter(a => a.endTime !== null && a.endDate != null).forEach(t => {
                const time = t.endTime.split(':');
                t.endDate.setHours(Number(time[0]));
                t.endDate.setMinutes(Number(time[1]));
                t.endDate.setSeconds(Number(time[2]));
            });

            // Sort availability ranges
            this.service.availabilityRanges.sort((a, b) => {
                if (a.startDate.getDate() === b.startDate.getDate()) {
                    // Sort by EndDate
                    if (!a.endDate) {
                        return 1;

                    } else if (!b.endDate) {
                        return -1;
                    }
                    return a.endDate.getDate() - b.endDate.getDate();

                } else {
                    // Sort by StartDate
                    return a.startDate.getDate() - b.startDate.getDate();
                }
            });

        }, () => this.router.navigate(['/']));
    }

    private checkDisplayOfLanguageFields(): void {
        if (this.crtLang === 'fr') {
            this.displayDescription = this.service.descriptionFR?.length > 0;
            this.displayInstruction = this.service.instructionFR?.length > 0;

        } else if (this.crtLang === 'de') {
            this.displayDescription = this.service.descriptionDE?.length > 0;
            this.displayInstruction = this.service.instructionDE?.length > 0;

        } else if (this.crtLang === 'en') {
            this.displayDescription = this.service.descriptionEN?.length > 0;
            this.displayInstruction = this.service.instructionEN?.length > 0;

        } else if (this.crtLang === 'it') {
            this.displayDescription = this.service.descriptionIT?.length > 0;
            this.displayInstruction = this.service.instructionIT?.length > 0;
        }
    }

    private clearUrlAfterActivation(): void {
        this.router.navigate(['/services/' + this.serviceId], {replaceUrl: true});
    }
}
